import { CameraAlt, PeopleAlt, PermContactCalendar, PictureAsPdf, Receipt, Verified } from '@mui/icons-material';
import { Box, Typography, Button, Avatar } from '@mui/material';
import { t } from 'i18next';
import { Link } from 'react-router-dom';
import { ToastOptions } from 'react-toastify';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { SignalRMessageTypeEnum } from 'src/shared/models/enums/signalRMessageType.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { Colors } from '../styles/colors';
import { SignalRExtraData } from './signalRMessageHandler';

export interface SignalRMessageTypeItem {
  label: string;
  color: string;
  icon: JSX.Element;
  type: SignalRMessageTypeEnum;
}

export const signalRToastMessageTypes: SignalRMessageTypeItem[] = [
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveSnapshotCSVFile}`,
    icon: <CameraAlt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveSnapshotCSVFile,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveAddressLabelsPDFFile}`,
    icon: <PictureAsPdf />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveAddressLabelsPDFFile,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveBookKeepingXLSXFile}`,
    icon: <Receipt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveBookKeepingXLSXFile,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveMembersCSVFile}`,
    icon: <PeopleAlt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveMembersCSVFile,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveBookKeepingSIEFile}`,
    icon: <Receipt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveBookKeepingSIEFile,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveMemberListPDFFile}`,
    icon: <PermContactCalendar />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveMemberListPDFFile,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveAutogiroMandateFile}`,
    icon: <Receipt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveAutogiroMandateFile,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.CreateMemberInvoicesDone}`,
    icon: <Receipt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.CreateMemberInvoicesDone,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.CreateInvoiceRemindersDone}`,
    icon: <Receipt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.CreateInvoiceRemindersDone,
  },
  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.SendSendingsByMissionIdDone}`,
    icon: <Receipt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.SendSendingsByMissionIdDone,
  },

  {
    label: `common:signalRMessageType.${SignalRMessageTypeEnum.ReceiveDepositsCSVFile}`,
    icon: <Receipt />,
    color: Colors.ColorBox.Blue,
    type: SignalRMessageTypeEnum.ReceiveDepositsCSVFile,
  },
];

export const getSignalRToastMessageType = (messageType: SignalRMessageTypeEnum): SignalRMessageTypeItem => {
  return signalRToastMessageTypes.find((item) => item.type === messageType) || signalRToastMessageTypes[0];
};

export const SignalRToastMessage = (
  messageType: SignalRMessageTypeEnum,
  message: string,
  data?: SignalRExtraData,
): { toastSettings: ToastOptions; toastMessage: React.ReactNode } => {
  const toastSettings: ToastOptions = {
    className: 'mui-toast member-export',
    autoClose: false,
    style: { width: 400 },
  };

  const getButtonLinkForInvoiceMission = () => {
    if (!data) return RoutesEnum.InvoiceMission;

    if (data.missionId && SignalRMessageTypeEnum.CreateMemberInvoicesDone) {
      return `${RoutesEnum.InvoiceMissionDetailSendingType}/${data.missionId}/${InvoiceSendingTypeIntegerEnum.NotSet}`;
    }

    if (data.missionId && SignalRMessageTypeEnum.CreateInvoiceRemindersDone) {
      return `${RoutesEnum.InvoiceMissionDetailSendingType}/${data.missionId}/${InvoiceSendingTypeIntegerEnum.NotSet}`;
    }

    if (data && SignalRMessageTypeEnum.SendSendingsByMissionIdDone) {
      return `${RoutesEnum.InvoiceMissionDetailSendingType}/${data}/${InvoiceSendingTypeIntegerEnum.NotSet}`;
    }

    return RoutesEnum.InvoiceMission;
  };

  const { label, icon, color } = getSignalRToastMessageType(messageType);

  // eslint-disable-next-line react/destructuring-assignment
  const DivContainer = (props) => <div>{props.children}</div>; // This prevents an error, https://github.com/fkhadra/react-toastify/issues/224
  let toastMessage = <></>;

  toastMessage = (
    <DivContainer>
      <Box sx={{ p: 3, pr: 3 }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ mr: 3, position: 'relative' }}>
            <Verified
              fontSize='small'
              sx={{
                color: 'green',
                position: 'absolute',
                zIndex: 2000,
                top: -5,
                right: -8,
                backgroundColor: '#fff',
                borderRadius: '100%',
              }}
            />
            <Avatar sx={{ bgcolor: color }}>{icon}</Avatar>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                {messageType === SignalRMessageTypeEnum.CreateMemberInvoicesDone ||
                messageType === SignalRMessageTypeEnum.CreateInvoiceRemindersDone ||
                messageType === SignalRMessageTypeEnum.SendSendingsByMissionIdDone ? (
                  <Typography variant='h5' sx={{ lineHeight: 1, fontWeight: 500 }} gutterBottom color='text.primary'>
                    {message}
                  </Typography>
                ) : (
                  <Typography variant='h5' sx={{ lineHeight: 1, fontWeight: 500 }} gutterBottom color='text.primary'>
                    {t(label)}
                  </Typography>
                )}
                {messageType !== SignalRMessageTypeEnum.CreateMemberInvoicesDone &&
                  messageType !== SignalRMessageTypeEnum.CreateInvoiceRemindersDone &&
                  messageType !== SignalRMessageTypeEnum.SendSendingsByMissionIdDone && (
                    <Typography color='text.secondary' variant='body2'>
                      {t('common:statusMessages.exportReady')}
                    </Typography>
                  )}
              </Box>
              <Typography textAlign='right' variant='body2' color='text.secondary' whiteSpace='nowrap'>
                {DateHelper.getTime(new Date())}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 2, width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size='small'
            sx={{ fontWeight: 500 }}
            component={Link as any}
            to={
              messageType === SignalRMessageTypeEnum.CreateMemberInvoicesDone ||
              messageType === SignalRMessageTypeEnum.SendSendingsByMissionIdDone ||
              messageType === SignalRMessageTypeEnum.CreateInvoiceRemindersDone
                ? getButtonLinkForInvoiceMission()
                : RoutesEnum.MyExports
            }
          >
            {t('common:button.goToMission')}
          </Button>
        </Box>
      </Box>
    </DivContainer>
  );

  return { toastSettings, toastMessage };
};
